import { useEffect, useRef } from "react";
import { useGT } from "gt-react";
import { X, Upload, AlertTriangle, Server, FileText } from "lucide-react";
import { useAppStore, importData } from "../store";
import { toast } from "./Toasts";

export function ImportPreviewDialog() {
  const t = useGT();
  const importPreview = useAppStore((s) => s.importPreview);
  const setImportPreview = useAppStore((s) => s.setImportPreview);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!importPreview) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setImportPreview(null);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [importPreview, setImportPreview]);

  useEffect(() => {
    if (importPreview && dialogRef.current) dialogRef.current.focus();
  }, [importPreview]);

  if (!importPreview) return null;

  const { data, warnings, summary } = importPreview;
  const envWarnings = warnings.filter((w) => w.type === "env-variable");
  const infoWarnings = warnings.filter((w) => w.type !== "env-variable");
  const sourceLabel = summary.source === "postman" ? "Postman Collection" : "Altair GraphQL";

  async function handleImport() {
    try {
      await importData(data);
      setImportPreview(null);
      toast(
        t('Imported {count} queries into {serverCount} servers', {
          count: summary.totalQueries,
          serverCount: summary.servers.length,
        }),
        "success",
      );
    } catch {
      toast(t("Failed to import data"), "error");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && setImportPreview(null)}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl border border-border bg-surface shadow-2xl outline-none"
      >
        <button
          onClick={() => setImportPreview(null)}
          className="absolute right-4 top-4 z-10 rounded p-1.5 text-zinc-500 hover:bg-surface-raised hover:text-zinc-300"
          title={t("Close")}
        >
          <X size={16} />
        </button>

        <div className="overflow-y-auto px-8 py-8">
          {/* Header */}
          <div className="mb-6 flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
              <Upload size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                {t("Import")}
              </div>
              <h1 className="mt-1 text-lg font-semibold tracking-tight text-zinc-100">
                {sourceLabel}
              </h1>
              {summary.collectionName && (
                <p className="mt-1 text-sm text-zinc-400">{summary.collectionName}</p>
              )}
            </div>
          </div>

          {/* Server list */}
          <div className="mb-6 space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              {t("Servers")}
            </h3>
            <div className="space-y-1.5">
              {summary.servers.map((s) => (
                <div
                  key={s.name}
                  className="flex items-center gap-2 rounded-lg border border-border bg-surface-raised px-3 py-2"
                >
                  <Server size={12} className="shrink-0 text-zinc-500" />
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">{s.name}</span>
                  <span className="flex items-center gap-1 text-xs text-zinc-500">
                    <FileText size={10} />
                    {s.queryCount}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500">
              {t("{total} queries total. Existing servers with the same name will be merged.", {
                total: summary.totalQueries,
              })}
            </p>
          </div>

          {/* Environment variable warnings */}
          {envWarnings.length > 0 && (
            <div className="mb-6 rounded-xl border border-warning/30 bg-warning-bg px-4 py-3">
              <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-warning">
                <AlertTriangle size={12} />
                {t("Environment Variables")}
              </div>
              <p className="text-xs leading-relaxed text-warning/80">
                {t("The following variables were found but are not imported automatically. Replace them with actual values in the server configuration after import.")}
              </p>
              <p className="mt-2 font-mono text-xs text-warning">
                {envWarnings.map((w) => w.message).join(", ")}
              </p>
            </div>
          )}

          {/* Info warnings */}
          {infoWarnings.length > 0 && (
            <div className="mb-6 space-y-1">
              {infoWarnings.map((w, i) => (
                <p key={i} className="text-xs text-zinc-500">
                  {w.message}
                </p>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              onClick={() => setImportPreview(null)}
              className="rounded px-3 py-1.5 text-xs text-zinc-400 hover:bg-surface-raised hover:text-zinc-200"
            >
              {t("Cancel")}
            </button>
            <button
              onClick={handleImport}
              className="rounded bg-brand px-4 py-2 text-xs font-semibold text-zinc-900 hover:bg-brand-light"
            >
              {t("Import")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
